import styled, { css } from 'styled-components';

import { Icon } from '@/ui/Icon';
import { BadgeLabel } from '@/ui/Label';
import { formatSingleDateDependingOnTimezone } from '@/utils/dates';
import { LearningCardProgressProps } from '@/administration/pages/Journey/common/atoms/LearningCard/LearningCardProgress';
import { useAdminContext } from '@/administration/store/AdminContext';

const ContainerStyled = styled.div<{ locked?: boolean }>`
  display: flex;
  flex-direction: column;
  padding: 0;
  ${({ locked }) =>
    locked &&
    css`
      opacity: 0.5;
    `}
`;

const SectionTitleStyled = styled.div`
  ${({ theme }) => theme.typography.title3};
  color: ${(p) => p.theme.colors.neutral.onBackground};
  margin-bottom: 8px;
  overflow-wrap: anywhere;
`;

const SectionDescriptionStyled = styled.div`
  ${(p) => p.theme.typography.body1};
  color: ${(p) => p.theme.tones.neutral[50]};
  overflow-wrap: anywhere;
`;

const MetaLineStyled = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 16px;
`;

const CompletedMetaLineStyled = styled(MetaLineStyled)`
  color: ${(p) => p.theme.colors.success.main};
`;

const MetaBlockStyled = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const IconStyled = styled(Icon)`
  color: ${(p) => p.theme.tones.neutral[50]};
`;

const MetaTextStyled = styled.div`
  ${({ theme }) => theme.typography.subheadline};
  color: ${(p) => p.theme.tones.neutral[50]};
`;

type JourneySectionCardProps = {
  title?: string;
  description?: string;
  totalEffort?: number;
  locked?: boolean;
  availabilityScheduledAtTimezone?: number;
  availabilityScheduledAt?: Date;
  isOptional?: boolean;
  progress?: LearningCardProgressProps['progress'];
  childrenCount?: number;
};

export const JourneySectionCard = ({
  title,
  description,
  childrenCount,
  locked,
  isOptional,
  availabilityScheduledAt,
  availabilityScheduledAtTimezone,
  progress,
}: JourneySectionCardProps): React.ReactElement => {
  const { isAdmin } = useAdminContext();
  const availabilityScheduledAtTimestamp = availabilityScheduledAt ? new Date(availabilityScheduledAt).getTime() : NaN;
  const isAvailabilityInFuture =
    Number.isFinite(availabilityScheduledAtTimestamp) && availabilityScheduledAtTimestamp > Date.now();
  const shouldShowAvailabilityBadge = Boolean(availabilityScheduledAt) && (isAdmin || isAvailabilityInFuture);

  const availabilityScheduledAtText = shouldShowAvailabilityBadge
    ? `Available on ${formatSingleDateDependingOnTimezone({
        date: availabilityScheduledAt || new Date(),
        formatOptions: {
          showTimezone: true,
          format: 'd MMM, HH:mm',
        },
        timezoneOffsetInMinutes: availabilityScheduledAtTimezone,
      })}`
    : undefined;

  return (
    <ContainerStyled locked={locked}>
      <SectionTitleStyled>{title}</SectionTitleStyled>
      <SectionDescriptionStyled>{description}</SectionDescriptionStyled>
      <MetaLineStyled>
        <MetaBlockStyled>
          <IconStyled icon={['far', locked ? 'lock' : 'layer-group']} />
          <MetaTextStyled>
            {childrenCount} {childrenCount === 1 ? 'item' : 'items'}
          </MetaTextStyled>
        </MetaBlockStyled>
      </MetaLineStyled>
      {(shouldShowAvailabilityBadge || isOptional) && (
        <MetaLineStyled>
          {isOptional && <BadgeLabel badgeVariant="badge-recommended" text="Optional" noIcon />}
          {shouldShowAvailabilityBadge && <BadgeLabel badgeVariant="badge-text" text={availabilityScheduledAtText} noIcon />}
        </MetaLineStyled>
      )}
      {progress?.isCompleted && (
        <CompletedMetaLineStyled>
          <Icon icon={'circle-check'} />
          Completed
        </CompletedMetaLineStyled>
      )}
    </ContainerStyled>
  );
};
